import { format, parseISO } from 'date-fns';

import {
    DrChronoTask,
    DrChronoTaskError,
    DrChronoTaskPayload,
    DrChronoTaskStatus,
    DrChronoTaskUpdate
} from '../types';

const CLOSED_CATEGORY = 'C';
const OPEN_CATEGORY = 'O';

/** New tasks need a status; prefer DrChrono's first open one. */
export const getDefaultStatusId = (statuses: DrChronoTaskStatus[] | undefined) =>
    (statuses?.find((status) => status.status_category === OPEN_CATEGORY) ?? statuses?.[0])?.id;

/** DrChrono's own status name, toned by whether its category is closed. */
export const getTaskStatus = (task: DrChronoTask, statuses: DrChronoTaskStatus[] | undefined) => {
    const status = statuses?.find(({ id }) => id === task.status_id);
    const isClosed = status?.status_category === CLOSED_CATEGORY;

    return { label: status?.name || (isClosed ? 'Closed' : 'Open'), isClosed };
};

const ERROR_MESSAGES: Record<string, string> = {
    drchrono_rate_limited: 'DrChrono is busy right now. Try again in a minute.',
    'DrChrono unreachable': 'DrChrono could not be reached. Try again.',
    drchrono_tasks_not_configured: 'DrChrono tasks are not set up for this organization.',
    member_not_linked: 'This member is not linked to a DrChrono patient.',
    invalid_request: 'Check the task details and try again.',
    task_not_found: 'This task no longer exists in DrChrono.'
};

export const getTaskErrorMessage = (error: DrChronoTaskError | null | undefined) => {
    if (!error) {
        return undefined;
    }

    return ERROR_MESSAGES[error.message] || error.message || 'The task could not be saved.';
};

export const TASK_PRIORITIES = [
    { value: 10, label: 'Low' },
    { value: 20, label: 'Medium' },
    { value: 30, label: 'High' },
    { value: 40, label: 'Urgent' }
] as const;

export const getPriorityLabel = (priority: number | null | undefined) =>
    TASK_PRIORITIES.find(({ value }) => value === priority)?.label;

/**
 * `due_at` must carry an offset, and rtm-api shows it to DrChrono in the practice's timezone.
 * Noon local keeps the picked calendar day across any US practice timezone; midnight would
 * slip to the previous day for practices west of the browser.
 */
export const toDueAt = (date: string) => {
    const [year, month, day] = date.split('-').map(Number);

    return new Date(year, month - 1, day, 12).toISOString();
};

export type TaskFormValues = {
    title: string;
    /** Edit only; a new task gets the default open status. */
    status: string;
    priority: string;
    due_date: string;
    note: string;
};

/** The local calendar day a `due_at` falls on, as a date input value. */
export const toDateInput = (dueAt: string | null | undefined) =>
    dueAt ? format(parseISO(dueAt), 'yyyy-MM-dd') : '';

/** A blank form, or one prefilled from a task. The note always starts empty: it adds a note. */
export const toTaskFormValues = (task?: DrChronoTask): TaskFormValues => ({
    title: task?.title ?? '',
    status: task?.status_id ? String(task.status_id) : '',
    priority: task?.priority ? String(task.priority) : '',
    due_date: toDateInput(task?.due_at),
    note: ''
});

// Only DrChrono's own priority values are sent; anything else sends none.
const toKnownPriority = (priority: string) =>
    TASK_PRIORITIES.find(({ value }) => String(value) === priority)?.value;

export const toTaskPayload = (
    { title, priority, due_date, note }: TaskFormValues,
    status_id: number
): DrChronoTaskPayload => {
    const knownPriority = toKnownPriority(priority);

    return {
        title: title.trim(),
        status_id,
        ...(knownPriority && { priority: knownPriority }),
        ...(due_date && { due_at: toDueAt(due_date) }),
        ...(note.trim() && { note: note.trim() })
    };
};

/**
 * Only what changed. A blank priority or due date is skipped: rtm-api cannot clear either, so
 * the form never offers clearing one that is set.
 */
export const toTaskUpdate = (values: TaskFormValues, task: DrChronoTask): DrChronoTaskUpdate => {
    const saved = toTaskFormValues(task);
    const title = values.title.trim();
    const priority = toKnownPriority(values.priority);
    const note = values.note.trim();

    return {
        ...(title !== saved.title.trim() && { title }),
        ...(values.status &&
            values.status !== saved.status && { status_id: Number(values.status) }),
        ...(priority && values.priority !== saved.priority && { priority }),
        ...(values.due_date &&
            values.due_date !== saved.due_date && { due_at: toDueAt(values.due_date) }),
        ...(note && { note })
    };
};
